// Restores the customer's explicitly-chosen country/language on a new visit (ZAJO-189).
//
// Shopify's own market resolution does not persist a manual localization-form choice across
// visits to the bare/default-market domain - the AC calls for that, but Shopify's own guidance
// warns against a *forced* redirect (bad for SEO bots, page caching, VPN false-positives). This
// mitigates that by running client-side only (crawlers without JS never trigger it) and firing
// at most once per tab session (sessionStorage guard), rather than on every navigation.
(() => {
  const PREFERENCE_STORAGE_KEY = 'zajo:localization-preference';
  const REDIRECT_ATTEMPTED_KEY = 'zajo:localization-redirect-attempted';

  let preference;
  try {
    const storedPreference = localStorage.getItem(PREFERENCE_STORAGE_KEY);
    preference = storedPreference ? JSON.parse(storedPreference) : null;
  } catch {
    return;
  }
  if (!preference || (!preference.country && !preference.language)) return;

  // Without a working sessionStorage the attempt below can never be recorded, so a stored
  // preference that the site can't actually reach (e.g. a since-deactivated market) would
  // resubmit the form on every single page load instead of at most once. Bail out entirely
  // rather than risk that loop - restoring the preference is a nice-to-have, an unguarded
  // redirect loop is not.
  try {
    if (sessionStorage.getItem(REDIRECT_ATTEMPTED_KEY) === 'true') return;
  } catch {
    return;
  }

  const currentCountry = document.documentElement.getAttribute('data-localization-country');
  const currentLanguage = document.documentElement.getAttribute('data-localization-language');
  const countryMatches = !preference.country || preference.country === currentCountry;
  const languageMatches = !preference.language || preference.language === currentLanguage;
  if (countryMatches && languageMatches) return;

  // The header, menu drawer and footer each render their own copy of this form, and each
  // only includes the fields its own section enabled - the header and menu drawer share the
  // header section's settings, but the footer has its own and can differ (e.g. footer-only
  // country selector). A market can also legitimately have only one available language, in
  // which case no form on the page renders a locale_code field at all. Matching on
  // preference.country/preference.language alone would accept a form for a field that is
  // already correct (e.g. country already matches but language doesn't) while ignoring the
  // field that actually needs changing, submitting a no-op and burning the one-per-tab
  // attempt. Match on which field is actually mismatched instead, so the chosen form can
  // always change something - restoring just the country (or just the language) once is
  // still strictly better than restoring neither.
  //
  // Read/write the hidden inputs directly rather than form.elements.country_code/locale_code:
  // the footer renders this form with include_native: true, which adds a native <select>
  // sharing the same `name` as the hidden input, so the bareword form property resolves to a
  // RadioNodeList there - whose `.value` getter is always "" and whose setter is a silent no-op.
  const localizationForm = Array.from(document.querySelectorAll('form.localization')).find((form) => (
    (!countryMatches && form.querySelector('input[type="hidden"][name="country_code"]'))
    || (!languageMatches && form.querySelector('input[type="hidden"][name="locale_code"]'))
  ));
  if (!localizationForm) return;

  const countryInput = localizationForm.querySelector('input[type="hidden"][name="country_code"]');
  const languageInput = localizationForm.querySelector('input[type="hidden"][name="locale_code"]');
  if (preference.country && countryInput) {
    countryInput.value = preference.country;
  }
  if (preference.language && languageInput) {
    languageInput.value = preference.language;
  }

  try {
    sessionStorage.setItem(REDIRECT_ATTEMPTED_KEY, 'true');
  } catch {
    return;
  }

  localizationForm.submit();
})();
